import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { PresenceSetFrame, RealtimeEvent } from "@lumika/shared";
import { chimeEnabledFor, playChime } from "../lib/sound";

/**
 * Subscribe to the operator-app realtime channel and translate
 * server-pushed events into react-query invalidations. Mounted once at
 * the top of `AppShell` so every page benefits.
 *
 * Wire format matches the shared `RealtimeEvent` union — same envelope
 * the widget gateway emits, so this hook is the single decoder used
 * across both surfaces (we feed it the operator stream today; the
 * widget stream can be added the same way later if needed).
 *
 * Reconnect: the native `WebSocket` API doesn't auto-reconnect. We
 * arrange that ourselves with a backoff loop, capped at 30s, so a
 * server restart or a Wi-Fi blip doesn't strand the page.
 *
 * Presence (UIUX §5.3): the hook also owns the one client→server
 * frame — `presence_set` — and a tiny in-memory viewer store fed by
 * `lead_presence` events. Presence NEVER triggers a query
 * invalidation; consumers read it via `RealtimeContext`. Ephemeral by
 * design: nothing survives a reload except re-announcing our own
 * state after reconnect.
 */

export interface LeadViewer {
  userId: string;
  state: "viewing" | "typing";
  /** Epoch ms of the last frame — entries expire locally at at+30s. */
  at: number;
}

export interface RealtimeApi {
  /** leadId → other operators currently on that thread (self excluded). */
  presenceByLead: ReadonlyMap<string, LeadViewer[]>;
  /**
   * Announce own presence. `leadId: null` = left the thread. Heartbeats
   * and per-5s typing throttling are handled here — callers just report
   * intent on open/type/leave.
   */
  setPresence: (leadId: string | null, state?: "viewing" | "typing") => void;
}

export const RealtimeContext = createContext<RealtimeApi>({
  presenceByLead: new Map(),
  setPresence: () => undefined,
});

/** Other operators on this lead — thread header + inbox row indicators. */
export function useLeadPresence(leadId: string | null | undefined): LeadViewer[] {
  const { presenceByLead } = useContext(RealtimeContext);
  return (leadId ? presenceByLead.get(leadId) : undefined) ?? [];
}

const PRESENCE_LOCAL_TTL_MS = 30_000;
const PRESENCE_HEARTBEAT_MS = 20_000;
const PRESENCE_THROTTLE_MS = 5_000;

export function useRealtime(ownUserId: string): RealtimeApi {
  const qc = useQueryClient();
  const [presenceByLead, setPresenceByLead] = useState<
    ReadonlyMap<string, LeadViewer[]>
  >(new Map());

  const socketRef = useRef<WebSocket | null>(null);
  // Own announced state — re-sent on heartbeat and after reconnect.
  const ownPresenceRef = useRef<{ leadId: string | null; state: "viewing" | "typing" }>({
    leadId: null,
    state: "viewing",
  });
  const lastSentRef = useRef<{ leadId: string | null; state: string; at: number }>({
    leadId: null,
    state: "viewing",
    at: 0,
  });

  useEffect(() => {
    let socket: WebSocket | null = null;
    let backoffMs = 1000;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let stopped = false;
    let hadConnected = false;

    function sendFrame(frame: PresenceSetFrame) {
      const s = socketRef.current;
      if (!s || s.readyState !== WebSocket.OPEN) return;
      try {
        s.send(JSON.stringify(frame));
        lastSentRef.current = { leadId: frame.leadId, state: frame.state, at: Date.now() };
      } catch {
        // Dropped frame — the next heartbeat re-announces.
      }
    }

    function applyPresence(event: Extract<RealtimeEvent, { type: "lead_presence" }>) {
      if (event.userId === ownUserId) return; // never render self
      setPresenceByLead((prev) => {
        const next = new Map(prev);
        const viewers = (next.get(event.leadId) ?? []).filter(
          (v) => v.userId !== event.userId,
        );
        if (event.state !== "left") {
          viewers.push({
            userId: event.userId,
            state: event.state,
            at: Date.now(),
          });
        }
        if (viewers.length === 0) next.delete(event.leadId);
        else next.set(event.leadId, viewers);
        return next;
      });
    }

    function connect() {
      if (stopped) return;
      const proto = window.location.protocol === "https:" ? "wss" : "ws";
      const apiBase = (import.meta.env.VITE_API_BASE_URL ?? "/api/v1").replace(/\/$/, "");
      const wsUrl = apiBase.startsWith("http")
        ? apiBase.replace(/^http/, "ws") + "/realtime"
        : `${proto}://${window.location.host}${apiBase}/realtime`;
      try {
        socket = new WebSocket(wsUrl);
      } catch (err) {
        console.warn("[realtime] failed to construct WebSocket", err);
        scheduleReconnect();
        return;
      }
      socketRef.current = socket;
      socket.addEventListener("open", () => {
        backoffMs = 1000; // reset on successful open
        // Events emitted while the socket was down are gone for good.
        // Cache freshness rides entirely on pushed invalidations (no
        // polling, focus refetch off), so resync everything this hook
        // normally keeps fresh after a reconnect.
        if (hadConnected) {
          qc.invalidateQueries({ queryKey: ["bdc"] });
          qc.invalidateQueries({ queryKey: ["notifications"] });
          qc.invalidateQueries({ queryKey: ["tasks"] });
        }
        hadConnected = true;
        // Re-announce our own presence — the server's map died with
        // the old socket, and peers expired us client-side.
        const own = ownPresenceRef.current;
        if (own.leadId) {
          sendFrame({ type: "presence_set", leadId: own.leadId, state: own.state });
        }
      });
      socket.addEventListener("message", (event) => {
        let parsed: RealtimeEvent | null = null;
        try {
          parsed = JSON.parse(event.data) as RealtimeEvent;
        } catch {
          return;
        }
        if (!parsed || typeof parsed.type !== "string") return;
        if (parsed.type === "lead_presence") {
          applyPresence(parsed);
          return; // presence never invalidates queries
        }
        handleEvent(parsed, qc);
      });
      socket.addEventListener("close", () => {
        socket = null;
        socketRef.current = null;
        scheduleReconnect();
      });
      socket.addEventListener("error", () => {
        // Errors trigger a close — let that handler reconnect.
      });
    }

    function scheduleReconnect() {
      if (stopped) return;
      if (reconnectTimer) return;
      reconnectTimer = setTimeout(() => {
        reconnectTimer = null;
        backoffMs = Math.min(backoffMs * 2, 30_000);
        connect();
      }, backoffMs);
    }

    connect();

    // Heartbeat: keep our own presence alive past the server's 30s TTL
    // while a thread is open.
    const heartbeat = setInterval(() => {
      const own = ownPresenceRef.current;
      if (own.leadId) {
        sendFrame({ type: "presence_set", leadId: own.leadId, state: own.state });
      }
    }, PRESENCE_HEARTBEAT_MS);

    // Local expiry: a peer whose heartbeats stopped (sleep, crash)
    // disappears at at+30s even if the server's "left" never arrived.
    const expiry = setInterval(() => {
      const cutoff = Date.now() - PRESENCE_LOCAL_TTL_MS;
      setPresenceByLead((prev) => {
        let changed = false;
        const next = new Map<string, LeadViewer[]>();
        for (const [leadId, viewers] of prev) {
          const fresh = viewers.filter((v) => v.at >= cutoff);
          if (fresh.length !== viewers.length) changed = true;
          if (fresh.length > 0) next.set(leadId, fresh);
        }
        return changed ? next : prev;
      });
    }, 10_000);

    return () => {
      stopped = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      clearInterval(heartbeat);
      clearInterval(expiry);
      socketRef.current = null;
      socket?.close();
    };
  }, [qc, ownUserId]);

  // Stable across renders (refs only) — consumers key announce/leave
  // effects on it without re-firing when unrelated presence changes.
  const setPresence = useCallback<RealtimeApi["setPresence"]>(
    (leadId, state = "viewing") => {
      const prev = ownPresenceRef.current;
      ownPresenceRef.current = { leadId, state };
      const last = lastSentRef.current;
      // Throttle repeats (typing fires per keystroke) to one frame
      // per 5s; transitions (new lead, state change, leave) go out
      // immediately.
      if (
        leadId === last.leadId &&
        state === last.state &&
        Date.now() - last.at < PRESENCE_THROTTLE_MS
      ) {
        return;
      }
      // Nothing to tell the server when we were already absent.
      if (leadId === null && prev.leadId === null && last.leadId === null) return;
      const s = socketRef.current;
      if (!s || s.readyState !== WebSocket.OPEN) return;
      try {
        s.send(JSON.stringify({ type: "presence_set", leadId, state } satisfies PresenceSetFrame));
        lastSentRef.current = { leadId, state, at: Date.now() };
      } catch {
        // Dropped frame — the next heartbeat re-announces.
      }
    },
    [],
  );

  return useMemo<RealtimeApi>(
    () => ({ presenceByLead, setPresence }),
    [presenceByLead, setPresence],
  );
}

/**
 * Map a single realtime event to the right query-cache invalidations.
 * Kept narrow and pattern-matched on `event.type` so any new event we
 * add to the shared union surfaces as a compile error here until we
 * pick its invalidation set.
 */
function handleEvent(
  event: RealtimeEvent,
  qc: ReturnType<typeof useQueryClient>,
): void {
  switch (event.type) {
    case "activity_created": {
      qc.invalidateQueries({ queryKey: ["bdc", "contact-activities", event.contactId] });
      qc.invalidateQueries({ queryKey: ["bdc", "activities"] });
      qc.invalidateQueries({ queryKey: ["bdc", "inbox"] });
      qc.invalidateQueries({ queryKey: ["bdc", "leads"] });
      qc.invalidateQueries({ queryKey: ["bdc", "contact-leads", event.contactId] });
      return;
    }
    case "lead_updated": {
      qc.invalidateQueries({ queryKey: ["bdc", "inbox"] });
      qc.invalidateQueries({ queryKey: ["bdc", "leads"] });
      qc.invalidateQueries({ queryKey: ["bdc", "contact-leads", event.contactId] });
      // V5 live calls emit lead_updated while the relay session UPDATES
      // an existing call activity (liveCall flag flips, V6 transcript
      // checkpoints rewrite the body) — no activity_created fires, so
      // the open thread must refetch activities here to render the
      // transcript-so-far.
      qc.invalidateQueries({ queryKey: ["bdc", "activities"] });
      return;
    }
    case "contact_updated": {
      qc.invalidateQueries({ queryKey: ["bdc", "contacts"] });
      qc.invalidateQueries({ queryKey: ["bdc", "contact", event.contactId] });
      qc.invalidateQueries({ queryKey: ["bdc", "contact-tags", event.contactId] });
      qc.invalidateQueries({ queryKey: ["bdc", "contact-tags-all"] });
      // Inbox rows and the leads board carry the contact's name/avatar
      // denormalized — a rename (or the AI filling in an anonymous
      // lead's name) must reach them too.
      qc.invalidateQueries({ queryKey: ["bdc", "inbox"] });
      qc.invalidateQueries({ queryKey: ["bdc", "leads"] });
      return;
    }
    case "lead_presence": {
      // Handled by the presence store in useRealtime — NEVER a query
      // invalidation (UIUX §5.3).
      return;
    }
    case "notification_created": {
      // A notification just landed for this user (e.g. a real-time AI
      // escalation). Refresh the bell's unread count and any open feed
      // immediately instead of waiting for the 60s poll. The
      // `["notifications"]` prefix covers both the count and list keys.
      qc.invalidateQueries({ queryKey: ["notifications"] });
      // Audible chime (UIUX §5.5) — escalations by default, SLA
      // breaches opt-in, never routine messages.
      if (chimeEnabledFor(event.eventKey)) playChime();
      // A task event (assigned / due / overdue) means the assignee's
      // Tasks board and the inbox needs-human surfacing changed too.
      if (event.eventKey?.startsWith("task.")) {
        qc.invalidateQueries({ queryKey: ["tasks"] });
        qc.invalidateQueries({ queryKey: ["bdc", "inbox"] });
        qc.invalidateQueries({ queryKey: ["bdc", "leads"] });
      }
      // AI- and human-side appointment events are the only realtime
      // signal the calendar gets — without this, an open calendar never
      // shows AI-booked appointments until a manual reload.
      if (
        event.eventKey?.startsWith("appointment.") ||
        event.eventKey?.startsWith("ai.appointment.")
      ) {
        qc.invalidateQueries({ queryKey: ["bdc", "appointments"] });
        // The per-contact readers (Contact 360's decision rail +
        // Appointments tab, lead detail's contact panel) key on
        // "appointments-contact", which the plural key above does NOT
        // prefix-match — without this they show the old book/reschedule
        // state until a manual reload. Same pair the manual booking
        // mutation invalidates.
        qc.invalidateQueries({ queryKey: ["bdc", "appointments-contact"] });
      }
      return;
    }
    case "connected":
    case "agent_start":
    case "turn_start":
    case "message_start":
    case "message_update":
    case "message_end":
    case "turn_end":
    case "agent_end":
    case "error":
      // Widget-stream events; not currently consumed on the operator app.
      return;
  }
}
